import type { Phone } from "@credit-acceptance/step6-bff-client";

import { RESIDENCE_OWNER_TYPE_OPTIONS } from "../constants";
import { formatPhoneOrDigits } from "../fields/PhoneNumberInput";
import { VERIFICATION_OUTCOME } from "./callScript.savePayload";
import { rankPhones, selectLivePhones } from "./callScriptResidence.mapping";
import type {
  ResidenceFieldKey,
  ResidenceQuestionFieldKey,
  ResidenceVerifiedFields,
} from "./callScriptResidence.types";
import type { ResidenceQuestionPkMap } from "./callScriptResidenceQuestions";
import { mergeString, toYesNo } from "./callScriptShared.payload";
import type { CallScriptApplicantRole } from "./callScriptShared.types";

// ---------------------------------------------------------------------------
// callScriptResidence.savePayload
//
// Builds the POST body for the residence ("Customer Verification") Call
// Script save, matching recap-service's CallScriptResidenceSaveRequest
// (forwarded verbatim by the BFF; the generated client types it `object`).
//
// recap writes back to CAS only the current address, phones, and the Do Not
// Call request. Mailing address and the Deal/down-payment block have no
// writeback — they're recorded on the call through `verificationAnswers[]`.
// ---------------------------------------------------------------------------

/** One question's verified-vs-stored answer comparison — same shape as the employment script's. */
export interface ResidenceCallScriptVerificationAnswer {
  /** Resolved from the `getResidenceCallScriptQuestions` catalog — see buildResidenceQuestionPkMap. */
  verificationQuestionPk: number;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

/** recap's SignerPutAddressEntry; an omitted key leaves the stored value untouched. */
export interface ResidenceCallScriptAddressEntry {
  /** Street number and name joined with a space. */
  line1?: string;
  /** Apartment number. */
  line2?: string;
  city?: string;
  state?: string;
  /** Exactly 5 digits. */
  zip?: string;
}

/** recap's SignerPutPhoneRow; an empty row leaves the phone at that position untouched. */
export interface ResidenceCallScriptPhoneRow {
  /** Exactly 10 digits. */
  number?: string;
}

/** Request body for the residence Call Script save (POST) endpoint. */
export interface ResidenceCallScriptSaveBody {
  applicantRole: CallScriptApplicantRole;
  phoneNumberDialed: string;
  /** One of {@link VERIFICATION_OUTCOME} — see computeResidenceVerificationOutcome. */
  verificationOutcome: string;
  /** Always "N" — CAPS2 hard-codes it for the residence script. */
  isVerificationOutsourced: "Y" | "N";
  isPhoneNoBad: "Y" | "N";
  /** ISO date verified on this call. */
  dob?: string;
  noteText: string;
  /** Must be non-empty — recap rejects the save otherwise. */
  verificationAnswers: ResidenceCallScriptVerificationAnswer[];
  addresses?: { current: ResidenceCallScriptAddressEntry };
  /** Positional against the applicant's live phones; a shorter list soft-deletes the trailing ones. */
  phones?: { rows: ResidenceCallScriptPhoneRow[] };
  /** Requests Do Not Call on the primary phone. */
  dncRequested: boolean;
}

/** Response from the residence Call Script save — persisted identifiers only, no full entry. */
export interface ResidenceCallScriptSaveResponse {
  verificationPhoneCallPk: number;
  verificationAnswerPks: number[];
  dpscNotePk?: number;
  /** `CVD` (driver) or `CVSS` (second signer). */
  dpscNoteType?: string;
  occurredAt: string;
}

/** A rendered field row, in screen order, for the note's `Label - value` lines. */
export interface ResidenceNoteField {
  key: ResidenceFieldKey;
  label: string;
}

/** Everything the payload builder needs beyond the ids in the URL path. */
export interface ResidenceCallScriptSaveContext {
  personPath: "driver" | "secondSigner";
  /** The analyst's free-text Verification Notes. */
  note: string;
  /** Visible field rows, in screen order — each verified one becomes a note line. */
  noteFields: ResidenceNoteField[];
  doNotContactPrimary: boolean;
  verifiedFields: ResidenceVerifiedFields;
  /** The "Saved Information" column — the fallback for any field left blank on the call. */
  savedFields: ResidenceVerifiedFields;
  /** Physical fields are a complete selected/standardized address, including intentional blanks. */
  physicalAddressComplete?: boolean;
  /** The applicant's phones from the dashboard, used to place Primary/Secondary edits positionally. */
  savedPhones?: Phone[];
  /** Field -> verificationQuestionPk, resolved from the fetched question catalog. */
  questionPkMap: ResidenceQuestionPkMap;
  /** The applicant's most recent saved call's outcome, if any — an earlier RPC carries forward. */
  previousVerificationOutcome?: string;
}

/**
 * Derives the residence call's outcome, porting CAPS2's
 * Step6ApplicantResidenceVerificationBackingBean.setValuesToVerificationPhoneCall:
 *   - once the applicant's previous call was RPC, every later call stays RPC;
 *   - otherwise no number dialed means no attempt was made;
 *   - a verified date of birth means the right party was reached;
 *   - absent one, a confirmed-bad original number means the lead isn't
 *     fundable, otherwise no RPC but no issues found.
 * The employment script's `computeVerificationOutcome` is the same shape,
 * keyed on hire date instead of date of birth and without the RPC carry-forward.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {string} [previousOutcome] - The most recent saved call's outcome.
 * @returns {string} One of the {@link VERIFICATION_OUTCOME} values.
 */
export function computeResidenceVerificationOutcome(
  verified: ResidenceVerifiedFields,
  previousOutcome?: string
): string {
  if (previousOutcome?.toLowerCase() === VERIFICATION_OUTCOME.RPC.toLowerCase()) {
    return VERIFICATION_OUTCOME.RPC;
  }
  if (!verified.phoneNumberDialed.replaceAll(/\D/gu, "")) {
    return VERIFICATION_OUTCOME.NO_ATTEMPT;
  }
  if (verified.dateOfBirth.trim()) {
    return VERIFICATION_OUTCOME.RPC;
  }
  return verified.wasOriginalNumberBad ? VERIFICATION_OUTCOME.NOT_FUNDABLE : VERIFICATION_OUTCOME.NO_RPC_NO_ISSUES;
}

/** Phone-valued fields, formatted xxx-xxx-xxxx in answers and notes. */
const PHONE_FIELDS = new Set<ResidenceFieldKey>([
  "phoneNumberDialed",
  "primaryNumber",
  "secondaryNumber",
  "landlordPhone",
]);

type ResidenceQuestionFieldValue = ResidenceVerifiedFields[ResidenceQuestionFieldKey];

/**
 * Whether a field value counts as unanswered. Booleans are never blank.
 * @param {ResidenceQuestionFieldValue} value - The raw field value.
 * @returns {boolean} Whether the value is blank.
 */
function isBlank(value: ResidenceQuestionFieldValue): boolean {
  return typeof value !== "boolean" && String(value).trim() === "";
}

/**
 * Renders a field's value the way the save contract's `answerEntered` /
 * `answerStored` carry it: Y/N for booleans, xxx-xxx-xxxx for phones, the
 * raw string or number otherwise.
 * @param {ResidenceQuestionFieldKey} field - Which field the value belongs to.
 * @param {ResidenceQuestionFieldValue} value - The field's value.
 * @returns {string} The wire representation.
 */
function toAnswerText(field: ResidenceQuestionFieldKey, value: ResidenceQuestionFieldValue): string {
  if (typeof value === "boolean") {
    return toYesNo(value);
  }
  const text = String(value);
  return PHONE_FIELDS.has(field) ? formatPhoneOrDigits(text) : text;
}

/**
 * Builds the per-question `verificationAnswers[]`. Mirrors the employment
 * script's `buildVerificationAnswers`: one row per field that both resolved a
 * `verificationQuestionPk` from the catalog AND was answered on the call;
 * `isAnswerMatch` is "Y" only when there is a stored value and it matches
 * the entered one case-insensitively.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {ResidenceVerifiedFields} saved - The "Saved Information" values.
 * @param {ResidenceQuestionPkMap} pkMap - Field -> verificationQuestionPk.
 * @returns {ResidenceCallScriptVerificationAnswer[]} The answer rows for the save request.
 */
export function buildResidenceVerificationAnswers(
  verified: ResidenceVerifiedFields,
  saved: ResidenceVerifiedFields,
  pkMap: ResidenceQuestionPkMap
): ResidenceCallScriptVerificationAnswer[] {
  const answers: ResidenceCallScriptVerificationAnswer[] = [];
  for (const field of Object.keys(pkMap) as ResidenceQuestionFieldKey[]) {
    const verificationQuestionPk = pkMap[field];
    const enteredValue = verified[field];
    if (verificationQuestionPk === undefined || isBlank(enteredValue)) {
      continue;
    }
    const answerEntered = toAnswerText(field, enteredValue);
    const storedValue = saved[field];
    const answerStored = isBlank(storedValue) ? "" : toAnswerText(field, storedValue);
    const isAnswerMatch = answerStored !== "" && answerEntered.toLowerCase() === answerStored.toLowerCase();
    answers.push({ verificationQuestionPk, answerEntered, answerStored, isAnswerMatch: toYesNo(isAnswerMatch) });
  }
  return answers;
}

const PHYSICAL_ADDRESS_FIELDS = [
  "physicalStreetNumber",
  "physicalStreetName",
  "physicalApartmentNumber",
  "physicalCity",
  "physicalState",
  "physicalZip",
] as const satisfies readonly ResidenceFieldKey[];

/**
 * Builds `addresses.current` when any physical-address field was verified;
 * partial answers fall back to saved values, while complete standardized
 * addresses preserve intentional blanks.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {ResidenceVerifiedFields} saved - The "Saved Information" values.
 * @param {boolean} complete - Whether verified fields form a complete address rather than partial answers.
 * @returns {ResidenceCallScriptAddressEntry | undefined} The entry, or undefined to leave the address untouched.
 */
function buildCurrentAddress(
  verified: ResidenceVerifiedFields,
  saved: ResidenceVerifiedFields,
  complete: boolean
): ResidenceCallScriptAddressEntry | undefined {
  if (PHYSICAL_ADDRESS_FIELDS.every((field) => verified[field].trim() === "")) {
    return undefined;
  }
  const merge = (field: (typeof PHYSICAL_ADDRESS_FIELDS)[number]) =>
    complete ? verified[field].trim() : mergeString(verified[field], saved[field]);
  const optional = (value: string) => (complete ? value : value || undefined);
  const line1 = [merge("physicalStreetNumber"), merge("physicalStreetName")].filter(Boolean).join(" ");
  const zip = merge("physicalZip").replaceAll(/\D/gu, "").slice(0, 5);
  return {
    line1: optional(line1),
    line2: optional(merge("physicalApartmentNumber")),
    city: optional(merge("physicalCity")),
    state: optional(merge("physicalState").toUpperCase()),
    zip: optional(zip),
  };
}

/**
 * Builds `phones.rows` when Primary/Secondary Number was verified to a new
 * 10-digit value. recap zips rows onto the applicant's live phones by
 * position and soft-deletes any it doesn't receive, so every live phone gets
 * a row (empty = untouched) and only the edited slots carry a number. Returns
 * undefined when the phone list is unknown, so a partial list can't delete.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {ResidenceVerifiedFields} saved - The "Saved Information" values.
 * @param {Phone[] | undefined} savedPhones - The applicant's dashboard phones.
 * @returns {{ rows: ResidenceCallScriptPhoneRow[] } | undefined} The section, or undefined to leave phones untouched.
 */
function buildPhoneRows(
  verified: ResidenceVerifiedFields,
  saved: ResidenceVerifiedFields,
  savedPhones: Phone[] | undefined
): { rows: ResidenceCallScriptPhoneRow[] } | undefined {
  if (savedPhones === undefined) {
    // The phone list is unknown (dashboard block absent), so a positional list
    // can't be built — sending a short one would soft-delete the applicant's
    // remaining phones. Leave phones untouched instead.
    return undefined;
  }
  const live = selectLivePhones(savedPhones);
  const ranked = rankPhones(live);
  const rows: ResidenceCallScriptPhoneRow[] = live.map(() => ({}));
  let changed = false;
  for (const [slot, field] of [
    [0, "primaryNumber"],
    [1, "secondaryNumber"],
  ] as const) {
    const digits = verified[field].replaceAll(/\D/gu, "");
    if (digits.length !== 10 || digits === saved[field].replaceAll(/\D/gu, "")) {
      continue;
    }
    const target = ranked[slot];
    // With no live phone for this slot the number is new, so it still has to
    // land on its own position — padding keeps Secondary at position 1 rather
    // than collapsing it onto Primary.
    const index = target ? live.indexOf(target) : slot;
    while (rows.length < index) {
      rows.push({});
    }
    rows[index] = { number: digits };
    changed = true;
  }
  return changed ? { rows } : undefined;
}

/** CAPS2's DPSC note header for each applicant role. */
export const RESIDENCE_NOTE_HEADER = {
  driver: "CUSTOMER VERIFICATION - FOR DRIVER",
  secondSigner: "CUSTOMER VERIFICATION - FOR 2ND SIGNER",
} as const;

const DATE_FIELDS = new Set<ResidenceFieldKey>(["dateOfBirth", "whenIsFinalAmountDue"]);

/**
 * Renders a verified value for a note line, or undefined when it's blank.
 * @param {ResidenceFieldKey} field - Which field the value belongs to.
 * @param {ResidenceVerifiedFields[ResidenceFieldKey]} value - The verified value.
 * @returns {string | undefined} The note text for the value.
 */
function toNoteValue(field: ResidenceFieldKey, value: ResidenceVerifiedFields[ResidenceFieldKey]): string | undefined {
  if (typeof value === "boolean") {
    return toYesNo(value);
  }
  if (typeof value === "number") {
    return value.toFixed(2);
  }
  const text = value.trim();
  if (!text) {
    return undefined;
  }
  if (PHONE_FIELDS.has(field)) {
    return formatPhoneOrDigits(text);
  }
  if (field === "residenceOwnerType") {
    return RESIDENCE_OWNER_TYPE_OPTIONS.find((option) => option.value === text)?.label ?? text;
  }
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(text);
  return DATE_FIELDS.has(field) && isoDate ? `${isoDate[2]}/${isoDate[3]}/${isoDate[1]}` : text;
}

/**
 * Builds the DPSC note text the way CAPS2 writes its CVD/CVSS notes: a
 * header, one `Label - value` line per verified field (including the
 * amounts and dates that have no verification question), then the free
 * text. recap persists `noteText` as a single note.
 * @param {"driver" | "secondSigner"} personPath - Which applicant.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {ResidenceNoteField[]} noteFields - Visible field rows, in screen order.
 * @param {string} note - The analyst's free-text notes.
 * @returns {string} The note text.
 */
export function buildResidenceNoteText(
  personPath: "driver" | "secondSigner",
  verified: ResidenceVerifiedFields,
  noteFields: ResidenceNoteField[],
  note: string
): string {
  const lines = noteFields.flatMap(({ key, label }) => {
    const value = toNoteValue(key, verified[key]);
    return value === undefined ? [] : [`${label} - ${value}`];
  });
  const header = lines.length > 0 ? [RESIDENCE_NOTE_HEADER[personPath]] : [];
  return [...header, ...lines, note.trim()].filter(Boolean).join("\n");
}

/**
 * Builds the residence Call Script save body from the modal's live state.
 * @param {ResidenceCallScriptSaveContext} context - The modal's captured values plus the ids it needs.
 * @returns {ResidenceCallScriptSaveBody} The POST body.
 */
export function buildResidenceCallScriptSaveBody(context: ResidenceCallScriptSaveContext): ResidenceCallScriptSaveBody {
  const { verifiedFields: verified, savedFields: saved } = context;
  const current = buildCurrentAddress(verified, saved, context.physicalAddressComplete ?? false);
  return {
    applicantRole: context.personPath === "driver" ? "DRIVER" : "SECOND_SIGNER",
    phoneNumberDialed: verified.phoneNumberDialed.replaceAll(/\D/gu, ""),
    verificationOutcome: computeResidenceVerificationOutcome(verified, context.previousVerificationOutcome),
    isVerificationOutsourced: "N",
    isPhoneNoBad: toYesNo(verified.wasOriginalNumberBad),
    dob: verified.dateOfBirth.trim() || undefined,
    noteText: buildResidenceNoteText(context.personPath, verified, context.noteFields, context.note),
    verificationAnswers: buildResidenceVerificationAnswers(verified, saved, context.questionPkMap),
    addresses: current ? { current } : undefined,
    phones: buildPhoneRows(verified, saved, context.savedPhones),
    dncRequested: context.doNotContactPrimary,
  };
}
